import React, { useMemo, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { Box } from '@mui/material';
import { AnyAction } from '@reduxjs/toolkit';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import {
    ContextActionType,
    EmptyBox,
    HeaderActionButton,
    HeaderListSkeleton,
    ListHeader,
    ListSkeleton,
    SkeletonLoader,
    useDumbActionsContext,
} from '@europrocurement/flexy-components';
import {
    DossierOpenSearchPrescriber,
    DossierOpenSearchPrescriberSelectorMain,
    DOSSIERS_OPEN_SEARCH_PRESCRIBER_SLICE_NAME,
    useFormaliteService,
} from '@europrocurement/l2d-domain';
import {
    dossiersBasePath,
    DossierTable,
    parcoursFormalitePathResolver,
    PossibleTypes,
    useDossierTable,
    useSearchBar,
} from '@europrocurement/l2d-modules';
import { DataSource } from '@europrocurement/l2d-redux-utils';
import { createFormalityFolioIcon, FaOptionIcon } from '@europrocurement/l2d-icons';

import { useClickActions, useInitialLoadingState, usePrescriber } from '@europrocurement/l2d-hooks';

interface FoliosProps {
    prescriberId?: number;
}

/**
 * Liste des dossiers de formalités liés à un prescripteur.
 * Utilise un slice Redux dédié pour éviter les conflits de filtres avec la liste générale.
 */
const Folios: React.FunctionComponent<FoliosProps> = function ({
    prescriberId: prescriberIdFromParent,
}) {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { prescriberId } = usePrescriber({ prescriberIdFromParent });
    const prescriberIdRef = useRef<number | undefined>();
    const [filtersSet, setFiltersSet] = useState(false);

    const datasource: DataSource<DossierOpenSearchPrescriber> = useSelector(
        DossierOpenSearchPrescriberSelectorMain,
    );
    const { isInitialLoading, isInError } = useInitialLoadingState(datasource);
    const { setIsSearchBarPath, setSearchBarType } = useSearchBar();

    const { getOpenSearchDossiersPrescriber } = useFormaliteService();
    const getOpenSearchDossiersPrescriberRef = React.useRef(getOpenSearchDossiersPrescriber);
    getOpenSearchDossiersPrescriberRef.current = getOpenSearchDossiersPrescriber;

    const { navigateOnClick } = useClickActions({ navigate });

    const { columns, filters, preFilters, rowActions } = useDossierTable({
        prescriberColumnEnabled: true,
        prescriberFilterEnabled: false,
        sliceName: DOSSIERS_OPEN_SEARCH_PRESCRIBER_SLICE_NAME,
        application: 'BACK',
    });

    const breadcrumbPath = useMemo(
        () => [
            {
                link: '/accueil',
                icon: {
                    icon: ['fasl', 'house'] as IconProp,
                    sx: { display: 'flex', alignItems: 'center' },
                },
            },
            { link: '/dossiers', path: 'Dossiers' },
            'Liste des dossiers du prescripteur',
        ],
        [],
    );

    React.useEffect(() => {
        setSearchBarType(PossibleTypes.FormalityFolioPrescriber);
        setIsSearchBarPath(true);
        return () => {
            setSearchBarType(undefined);
            setIsSearchBarPath(false);
        };
    }, [setIsSearchBarPath, setSearchBarType]);

    React.useEffect(() => {
        if (prescriberId && prescriberId !== prescriberIdRef.current) {
            dispatch({
                type: `${DOSSIERS_OPEN_SEARCH_PRESCRIBER_SLICE_NAME}/setmainFilter`,
                payload: { key: 'prescriberId', value: [prescriberId] },
            });
            prescriberIdRef.current = prescriberId;
            setFiltersSet(true);
        }
    }, [dispatch, prescriberId]);

    React.useEffect(() => {
        if (filtersSet) {
            dispatch(getOpenSearchDossiersPrescriberRef.current({}) as unknown as AnyAction);
        }
    }, [dispatch, filtersSet]);

    const handleRowClick = useCallback(
        ({ id }: DossierOpenSearchPrescriber) => {
            navigateOnClick({ path: `/${dossiersBasePath}/${id}` });
        },
        [navigateOnClick],
    );

    const handleRowWheelClick = useCallback(
        ({ id }: DossierOpenSearchPrescriber) => {
            navigateOnClick({ path: `/${dossiersBasePath}/${id}`, openNewTab: true });
        },
        [navigateOnClick],
    );

    const handleCreateDossier = useCallback(() => {
        navigate(parcoursFormalitePathResolver('stepPrescriber'));
    }, [navigate]);

    const contextActions: ContextActionType[] = useMemo(
        () => [
            {
                name: 'Nouveau dossier',
                icon: <FaOptionIcon {...createFormalityFolioIcon.props} />,
                action: handleCreateDossier,
            },
        ],
        [handleCreateDossier],
    );

    useDumbActionsContext(contextActions);

    const errorContent = useMemo(
        () => (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    width: '100%',
                }}
            >
                <EmptyBox
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-evenly',
                        height: '25%',
                        width: '50%',
                    }}
                    text="Une erreur s'est produite lors de la récupération des dossiers."
                    subText="Veuillez recharger la page. Si le problème persiste, contactez votre administrateur."
                />
            </Box>
        ),
        [],
    );

    if (!filtersSet) {
        return <ListSkeleton columns={6} />;
    }

    return (
        <SkeletonLoader
            isLoading={isInitialLoading}
            skeleton={
                <>
                    <HeaderListSkeleton />
                    <ListSkeleton columns={6} />
                </>
            }
            isInError={isInError}
            errorChildren={errorContent}
        >
            <ListHeader
                breadcrumbPath={breadcrumbPath}
                actions={
                    <HeaderActionButton
                        label="Créer un nouveau dossier de formalité"
                        icon={createFormalityFolioIcon}
                        onClick={handleCreateDossier}
                    />
                }
            />

            {prescriberIdRef.current && (
                <DossierTable
                    datasource={datasource}
                    columns={columns}
                    filters={filters || []}
                    preFilters={preFilters}
                    rowActions={rowActions}
                    fetchData={getOpenSearchDossiersPrescriberRef.current}
                    onRowClick={handleRowClick}
                    onWheelClick={handleRowWheelClick}
                />
            )}
        </SkeletonLoader>
    );
};

export default Folios;
